import { FC, useState } from 'react'

import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'

import { useAppDispatch } from 'src/Hooks/redux'
import { FullScreenModal } from 'src/Components/FullScreenModal'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { MAX_MOBILE_WIDTH } from 'src/Styled/variables'
import { CTAButton } from 'src/Components/Buttons/CTAButton'
import { PrimaryDottedButton } from 'src/Components/Buttons/PrimaryDottedButton'
import {
  goalGetMoreVideosEvent,
  goalGetMoreVideoViewsEvent,
  popupGoalSelectedEvent,
} from 'src/Helpers/TagManager/TikTokVideoBasedEvents'
import { getScheduledTikTokCampaign } from 'src/Redux/schedule-process/tiktokCampaign/selectors'
import { getUserId } from 'src/Redux/auth-process/userSlice/selectors'
import { PrimaryRadioWithOverlay } from 'src/Components/PrimaryRadioWithOverlay/RadioInput'
import { TikTokCampaignPricingModelEnum } from 'src/Types/campaigns'
import { updateTikTokCampaign } from 'src/Redux/schedule-process/tiktokCampaign/api-actions'
import { resetAudienceTiktok } from 'src/Redux/schedule-process/audienceTiktokSlice'

import { CreatorTypeEnum } from '../../TiktokScheduleParams/CreatorTypeParameter'

const StyledPrimaryRadioWithOverlay = styled(PrimaryRadioWithOverlay)`
  gap: 0px;
  padding: 16px;
`

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 100vh;
  padding: 24px 16px 65px 16px;
  flex-direction: column;
  gap: 24px;
  @media screen and (min-width: ${MAX_MOBILE_WIDTH}) {
    display: flex;
    margin: 0 auto;
    width: 600px;
    padding: 64px 0px 65px 0px;
    align-items: center;
  }
`
const SubContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  overflow-x: scroll;
  height: calc(100vh - 118px);
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  @media screen and (min-width: ${MAX_MOBILE_WIDTH}) {
    height: calc(100vh - 200px);
  }
`

const FooterContainer = styled.div`
  width: 100%;
  display: flex;
  padding: 8px 16px;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 24px;
  position: fixed;
  z-index: 2;
  left: 0px;
  bottom: 0px;
  border-top: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};
`

const StyledCTAButton = styled(CTAButton)`
  padding-left: 40px;
  padding-right: 40px;
`
const Title = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  text-align: start;
  width: 100%;
  font-family: 'Segoe UI';
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  flex: 1 0 0;
  padding-left: 30px;
`

const LabelTitle = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
`
const Description = styled(LabelTitle)`
  font-weight: 400;
`

const Tags = styled.div`
  display: flex;
  align-items: flex-start;
  align-content: flex-start;
  gap: 4px;
  align-self: stretch;
  flex-wrap: wrap;
  margin-top: 8px;
`
const Tag = styled.div`
  display: flex;
  height: 24px;
  padding: 0 6px;
  align-items: center;
  gap: 4px;
  overflow: hidden;
  color: ${({ theme }) => theme.color.darkGrey};
  text-overflow: ellipsis;
  border-radius: 16px;
  background: ${({ theme }) => theme.color.lightGrey};

  font-family: 'Segoe UI';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`

const pricingModelToCreatorType = (
  pricingModel?: TikTokCampaignPricingModelEnum | null,
): CreatorTypeEnum | null => {
  if (pricingModel === TikTokCampaignPricingModelEnum.VideosBased) {
    return CreatorTypeEnum.Smaller
  }
  if (pricingModel === TikTokCampaignPricingModelEnum.ViewsBased) {
    return CreatorTypeEnum.Bigger
  }
  return null
}

const creatorTypeToPricingModel = (
  creatorType: CreatorTypeEnum,
): TikTokCampaignPricingModelEnum =>
  creatorType === CreatorTypeEnum.Smaller
    ? TikTokCampaignPricingModelEnum.VideosBased
    : TikTokCampaignPricingModelEnum.ViewsBased

export const TiktokScheduleCreatorTypeModal: FC = () => {
  const dispatch = useAppDispatch()
  const userId = useSelector(getUserId)
  const scheduledCampaign = useSelector(getScheduledTikTokCampaign)
  const { number } = useParams()
  const { setSearch } = useCustomSearchParams()
  const campaignId = Number(scheduledCampaign.id) || Number(number)

  const [value, setValue] = useState<CreatorTypeEnum | null>(() =>
    pricingModelToCreatorType(scheduledCampaign.pricingModel),
  )

  const handleChange = (creatorType: CreatorTypeEnum): void => {
    setValue(creatorType)

    if (creatorType === CreatorTypeEnum.Bigger) {
      goalGetMoreVideoViewsEvent({
        userId: String(userId),
        campaignType: scheduledCampaign.type,
        campaignId,
      })
    }
    if (creatorType === CreatorTypeEnum.Smaller) {
      goalGetMoreVideosEvent({
        userId: String(userId),
        campaignType: scheduledCampaign.type,
        campaignId,
      })
    }
  }

  const handleClose = (): void => {
    setSearch({ modal: null })
  }

  const handleSave = (): void => {
    if (!value || !campaignId) {
      return
    }
    const nextPricingModel = creatorTypeToPricingModel(value)
    if (nextPricingModel !== scheduledCampaign.pricingModel) {
      dispatch(resetAudienceTiktok())
    }
    void dispatch(
      updateTikTokCampaign({
        id: campaignId,
        pricingModel: nextPricingModel,
      }),
    )
    setSearch({ modal: null })
    popupGoalSelectedEvent({
      userId: String(userId),
      campaignType: scheduledCampaign.type,
      campaignId,
    })
  }

  return (
    <FullScreenModal
      showCloseButton
      open
      onCancel={handleClose}
      prefixCls='creatorType'
    >
      <Container>
        <Title>What's your campaign goal?</Title>
        <SubContainer>
          <StyledPrimaryRadioWithOverlay
            label={<LabelTitle>Get more creators to use your sound</LabelTitle>}
            id={CreatorTypeEnum.Smaller}
            name={CreatorTypeEnum.Smaller}
            checked={CreatorTypeEnum.Smaller === value}
            onChange={() => handleChange(CreatorTypeEnum.Smaller)}
          >
            <Content>
              <Description>
                More creators will use your sound in TikTok videos, helping your
                song build activity, trigger the algorithm, and increase its
                chances of going viral or reaching the TikTok charts.
              </Description>
              <Tags>
                <Tag>More videos</Tag>
                <Tag>More creators</Tag>
                <Tag>Discovery boost</Tag>
              </Tags>
            </Content>
          </StyledPrimaryRadioWithOverlay>
          <StyledPrimaryRadioWithOverlay
            label={<LabelTitle>Get more video views</LabelTitle>}
            id={CreatorTypeEnum.Bigger}
            name={CreatorTypeEnum.Bigger}
            checked={CreatorTypeEnum.Bigger === value}
            onChange={() => handleChange(CreatorTypeEnum.Bigger)}
          >
            <Content>
              <Description>
                Fewer creators with larger audiences will create videos with
                your sound, helping your song get more views on TikTok.
              </Description>
              <Tags>
                <Tag>Fewer videos</Tag>
                <Tag>More views</Tag>
                <Tag>Audience reach</Tag>
              </Tags>
            </Content>
          </StyledPrimaryRadioWithOverlay>

          <FooterContainer>
            <PrimaryDottedButton onClick={handleClose}>
              Go Back
            </PrimaryDottedButton>
            <StyledCTAButton onClick={handleSave} disabled={!value}>
              Confirm goal
            </StyledCTAButton>
          </FooterContainer>
        </SubContainer>
      </Container>
    </FullScreenModal>
  )
}
